import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, MessageCircle, MapPin, Check } from "lucide-react";
import { useLenis } from "@/hooks/useLenis";
import { useSEO } from "@/components/site/SEO";
import { SITE, whatsappLink } from "@/config/site";
import { track } from "@/lib/analytics";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { FloatingCTA } from "@/components/site/FloatingCTA";
import { Marquee } from "@/components/site/Marquee";
import { Services } from "@/sections/Services";
import { Pricing } from "@/sections/Pricing";
import { HowItWorks } from "@/sections/HowItWorks";
import { QuoteForm } from "@/sections/QuoteForm";
import { FAQ } from "@/sections/FAQ";
import { Reveal } from "@/components/site/Reveal";
import { LOCATIONS, IMAGES, WHY_CHOOSE } from "@/data/content";

// Reusable local SEO landing page template.
export default function LocationPage() {
  const { slug } = useParams();
  const data = LOCATIONS[slug];
  useLenis();
  useSEO({
    title: data ? `${data.title} | GoCleaning Dublin` : "Cleaning | GoCleaning Dublin",
    description: data
      ? `${data.service} in ${data.area}. Professional, insured move-out cleaning with clear starting prices. Send photos for a fast quote across ${SITE.serviceArea}.`
      : "",
    canonical: `${SITE.url}/${slug}`,
  });

  if (!data) return <Navigate to="/" replace />;

  return (
    <div className="App bg-white">
      <Header />
      <main>
        <section className="relative overflow-hidden bg-white pt-32 pb-16 sm:pt-36" data-testid="location-hero">
          <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
            <Reveal>
              <Link to="/" className="text-sm font-semibold text-emerald-700 hover:underline">← Back to home</Link>
              <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-emerald-700">
                <MapPin size={13} /> {data.area}
              </div>
              <h1 className="mt-5 font-display text-5xl font-black leading-[1.03] tracking-tighter text-slate-900 sm:text-6xl">
                {data.title}
              </h1>
              <p className="mt-5 max-w-xl text-lg text-slate-600">
                {data.service} in {data.area} for tenants, landlords and property managers. Professional,
                reliable move-out cleaning with clear starting prices. Send photos or a short video and
                receive an accurate quote quickly.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a href="#quote" data-testid="location-quote-btn" className="group inline-flex items-center justify-center gap-2 rounded-full bg-emerald-700 px-7 py-4 text-base font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-emerald-800">
                  Get a Free Quote <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </a>
                <a href={whatsappLink()} onClick={() => track.whatsappClick("location")} target="_blank" rel="noreferrer" data-testid="location-whatsapp-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-slate-200 px-7 py-4 text-base font-bold text-slate-800 transition-all hover:border-[#25D366]">
                  <MessageCircle size={18} className="text-[#25D366]" /> WhatsApp Us
                </a>
              </div>
              <ul className="mt-8 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {WHY_CHOOSE.slice(0, 4).map((w) => (
                  <li key={w} className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <Check size={16} className="text-emerald-600" /> {w}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="overflow-hidden rounded-[2rem] shadow-[0_30px_80px_rgba(4,120,87,0.15)] max-h-[70vh]">
                <img src={IMAGES.emptyRoom} alt={`Freshly cleaned empty property in ${data.area} ready for handover`} className="aspect-[4/5] w-full object-cover" />
              </div>
            </Reveal>
          </div>
        </section>
        <Marquee />
        <Services />
        <Pricing />
        <HowItWorks />
        <QuoteForm sourcePage={`/${slug}`} prefillArea={data.area} />
        <FAQ />
      </main>
      <Footer />
      <FloatingCTA />
    </div>
  );
}
